'use client'

import { memo } from 'react'
import { motion } from 'framer-motion'
import { Check, DatabaseZap, Lock, Server, ShieldCheck, WifiOff } from 'lucide-react'
import { NinxlEvervaultShell } from './ninxl-evervault-shell'
import { NinxlScrollReveal } from './ninxl-scroll-reveal'

const PRIVACY_POINTS = [
  '100% local inference, voice, and tool execution',
  'On-device data stays on your system',
  'Cloud credits run under Zero Data Retention (ZDR)',
  'Explicit per-tool approvals · no silent telemetry',
] as const

const COMPLIANCE_BADGES = [
  {
    title: 'CASA Certified',
    desc: 'Independent security audit for desktop apps.',
    icon: ShieldCheck,
  },
  {
    title: 'SOC 2 in Progress',
    desc: 'Type II audit underway for data handling & access controls.',
    icon: Lock,
  },
  {
    title: 'ZDR Policy',
    desc: 'On-device stays local. Optional cloud credits use zero data retention.',
    icon: DatabaseZap,
  },
] as const

/** Animated privacy flow: You → NINX on device → Cloud opt-in */
const PrivacyFlowDiagram = memo(function PrivacyFlowDiagram() {
  return (
    <div className="ninxl-privacy-flow craft-foundation__flow relative mx-auto mb-6 max-w-sm rounded-2xl border border-[rgba(196,164,132,0.35)] bg-[rgba(255,252,246,0.72)] p-5 backdrop-blur-[2px]">
      <svg viewBox="0 0 320 120" className="h-auto w-full" aria-hidden>
        <rect
          x="8"
          y="42"
          width="72"
          height="36"
          rx="10"
          fill="rgba(255,252,246,0.95)"
          stroke="rgba(196,164,132,0.4)"
        />
        <text
          x="44"
          y="64"
          textAnchor="middle"
          className="fill-[#5d5d5d] text-[11px] font-semibold"
        >
          You
        </text>

        <rect
          x="118"
          y="32"
          width="84"
          height="56"
          rx="12"
          fill="rgba(33,114,141,0.1)"
          stroke="#21728d"
          strokeWidth="1.5"
        />
        <text
          x="160"
          y="58"
          textAnchor="middle"
          className="fill-[#134b5f] text-[11px] font-bold"
        >
          NINX
        </text>
        <text
          x="160"
          y="74"
          textAnchor="middle"
          className="fill-[#21728d] text-[9px]"
        >
          On your Mac
        </text>

        <rect
          x="240"
          y="42"
          width="72"
          height="36"
          rx="10"
          fill="rgba(255,252,246,0.55)"
          stroke="rgba(136,161,172,0.35)"
          strokeDasharray="4 3"
        />
        <text
          x="276"
          y="58"
          textAnchor="middle"
          className="fill-[#88a1ac] text-[10px]"
        >
          Cloud
        </text>
        <text
          x="276"
          y="70"
          textAnchor="middle"
          className="fill-[#88a1ac] text-[8px]"
        >
          opt-in only
        </text>

        <line
          x1="80"
          y1="60"
          x2="118"
          y2="60"
          className="ninxl-data-flow-line"
          stroke="#21728d"
          strokeWidth="2"
        />
        <line
          x1="202"
          y1="60"
          x2="240"
          y2="60"
          stroke="rgba(136,161,172,0.3)"
          strokeWidth="1.5"
          strokeDasharray="5 4"
        />
      </svg>
      <div className="flex items-center justify-center gap-4 text-[10px] text-[#88a1ac]">
        <span className="flex items-center gap-1">
          <WifiOff className="h-3 w-3" aria-hidden /> Works offline
        </span>
        <span className="flex items-center gap-1">
          <Server className="h-3 w-3" aria-hidden /> No server required
        </span>
      </div>
    </div>
  )
})

/**
 * baby: Built Different — restored cipher (Evervault) + flow diagram UX.
 * Background stays transparent so kraft craft page wash shows through.
 */
export const NinxlLocalByDesign = memo(function NinxlLocalByDesign() {
  return (
    <section
      className="craft-foundation relative overflow-hidden"
      data-testid="ninxl-local-by-design"
    >
      {/* baby: Soft teal wash only — no solid slate slab that fights craft kraft. */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_85%_55%_at_50%_-5%,rgba(33,114,141,0.07),transparent_62%)]"
        aria-hidden
      />

      <div className="mx-auto max-w-5xl">
        <div className="grid items-center gap-10 md:grid-cols-12 md:gap-12">
          <NinxlScrollReveal variant="left" className="md:col-span-5">
            <div
              className="mb-4 h-px w-14 bg-gradient-to-r from-[#21728d]/45 to-transparent"
              aria-hidden
            />
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#21728d]">
              Built different
            </p>
            <h3 className="mb-4 text-3xl font-semibold leading-[1.1] tracking-tight text-[var(--craft-ink,#292929)] md:text-4xl">
              Local by design.
              <br />
              Powerful by default.
            </h3>
            <p className="mb-6 text-base leading-relaxed text-[var(--craft-mute,#5d5d5d)] md:text-lg">
              NINX was built from the ground up to keep your work private and responsive without
              ever talking to a server unless you choose.
            </p>

            <PrivacyFlowDiagram />

            <div className="space-y-3 text-sm">
              {PRIVACY_POINTS.map((line, i) => (
                <motion.div
                  key={line}
                  className="flex items-start gap-3 text-[var(--craft-ink,#292929)]"
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.45 }}
                >
                  <div className="mt-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#21728d]/10">
                    <Check className="h-2.5 w-2.5 text-[#21728d]" aria-hidden />
                  </div>
                  {line}
                </motion.div>
              ))}
            </div>
          </NinxlScrollReveal>

          <NinxlScrollReveal variant="right" delay={0.1} className="md:col-span-7">
            <div className="ninxl-circuit-panel craft-foundation__cipher relative overflow-hidden rounded-3xl border border-[rgba(196,164,132,0.4)] bg-[rgba(255,252,246,0.55)] text-sm shadow-[0_12px_36px_rgba(20,32,40,0.06)] backdrop-blur-[2px]">
              <NinxlEvervaultShell
                roundedClassName="rounded-3xl"
                contentClassName="p-7 text-left md:p-8"
              >
                <div className="mb-5 text-xs uppercase tracking-widest text-[var(--craft-mute,#5d5d5d)]">
                  Security &amp; Compliance
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-5">
                  {COMPLIANCE_BADGES.map((badge, i) => (
                    <motion.div
                      key={badge.title}
                      className="ninxl-compliance-badge rounded-2xl border border-[rgba(196,164,132,0.32)] bg-[rgba(255,252,246,0.72)] p-4"
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + i * 0.1, duration: 0.45 }}
                    >
                      <div className="mb-2 inline-flex rounded-xl bg-[#21728d]/10 p-2 text-[#21728d]">
                        <badge.icon className="h-4 w-4" aria-hidden />
                      </div>
                      <div className="mb-1 font-semibold text-[var(--craft-ink,#292929)]">
                        {badge.title}
                      </div>
                      <div className="text-xs leading-relaxed text-[var(--craft-mute,#5d5d5d)]">
                        {badge.desc}
                      </div>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-7 flex items-center gap-2 border-t border-[rgba(196,164,132,0.28)] pt-5 text-xs text-[var(--craft-mute,#5d5d5d)]">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-[#21728d]" aria-hidden />
                  Your data is processed on the machine you own. Full stop.
                </div>
              </NinxlEvervaultShell>
            </div>
          </NinxlScrollReveal>
        </div>
      </div>
    </section>
  )
})
